import type { ReactNode } from 'react'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { ChartCard } from '@/components/shared/charts/ChartCard'
import { SegmentedControl } from '@/components/shared/layout'
import {
  OverviewCardFooter,
  OverviewCardTitle,
  OverviewTabs,
} from '@/components/shared/overview/OverviewBreakdownCard'

/**
 * Whole-tab states of Analytics → Overview that have no data to draw yet
 * (Paper "Analytics · Overview · Loading" and "· Tracking not installed").
 * Both keep the loaded tab's geometry: the same KPI strip, chart cards, live
 * panel and 2×2 cards, with labels in place and values as hairline bars or dashes.
 */

const noop = () => {}

function Bar({ w, h, className }: { w: number | string; h: number; className?: string }) {
  return <Skeleton className={cn('shrink-0', className)} style={{ width: w, height: h }} />
}

/* ─── KPI strip + chart cards ────────────────────────────────────────── */

type TileSpec = { label: string; value: number; delta?: number }

/** Secondary metrics only: visitors, views and revenue live in the chart cards */
const TRAFFIC_TILES: TileSpec[] = [
  { label: 'Bounce', value: 37, delta: 27 },
  { label: 'Time on site', value: 74, delta: 27 },
]
const REVENUE_TILES: TileSpec[] = [
  { label: 'Revenue per visitor', value: 62 },
  { label: 'Conversion rate', value: 49 },
]

function OverviewTiles({ showRevenue, mode }: { showRevenue: boolean; mode: 'loading' | 'empty' }) {
  const tiles = showRevenue ? [...TRAFFIC_TILES, ...REVENUE_TILES] : TRAFFIC_TILES
  return (
    <Card className="gap-0 py-0">
      <div className="flex overflow-x-auto scrollbar-hide">
        {tiles.map((t, i) => (
          <div
            key={t.label}
            className={cn('flex min-w-[140px] flex-1 basis-0 flex-col gap-1.5 px-[18px] py-4', i > 0 && 'border-l border-border')}
          >
            <span className="whitespace-nowrap text-xs leading-4 text-muted-foreground">{t.label}</span>
            {mode === 'loading' ? (
              <span className="flex items-baseline gap-1.5">
                <span className="flex h-7 items-center">
                  <Bar w={t.value} h={22} />
                </span>
                {t.delta && (
                  <span className="flex h-4 items-center">
                    <Bar w={t.delta} h={12} />
                  </span>
                )}
              </span>
            ) : (
              <span className="text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground/25">–</span>
            )}
          </div>
        ))}
      </div>
    </Card>
  )
}

/**
 * The chart card plot without data (Paper "Charts · Card pattern", loading):
 * 200px tall, 164px plot, four dotted gridlines, Y labels left in a 44px
 * gutter, real dates under the plot. Loading draws flat bars, all one height,
 * so a loading chart never reads as a trend.
 */
const PLOT_H = 164
const GRID_TOPS = [8, 60, 112, 164]

function PlotFrame({ ticks, mode }: { ticks: string[] | null; mode: 'loading' | 'empty' }) {
  const columns = ticks ?? Array.from({ length: 7 }, () => '')
  return (
    <div className="relative h-[200px]" aria-hidden>
      {GRID_TOPS.map((y) => (
        <div
          key={y}
          className="absolute right-0 left-11 border-t border-dotted border-foreground/[0.07]"
          style={{ top: y }}
        />
      ))}
      {mode === 'loading' ? (
        GRID_TOPS.map((y) => <Skeleton key={y} className="absolute left-0 h-2.5 w-6 rounded-[3px]" style={{ top: y - 5 }} />)
      ) : (
        <span className="absolute left-0 text-[11px] leading-none text-ui-meta" style={{ top: PLOT_H - 5 }}>0</span>
      )}
      <div className="absolute inset-y-0 right-0 left-11 flex">
        {columns.map((label, i) => (
          <div key={i} className="relative flex flex-1 justify-center">
            {mode === 'loading' && (
              <Skeleton className="absolute w-4 rounded-[4px]" style={{ top: PLOT_H - 66, height: 66 }} />
            )}
            {label ? (
              <span
                className={cn('absolute whitespace-nowrap text-[11px] leading-none text-ui-meta', i % 2 === 1 && 'max-sm:hidden')}
                style={{ top: PLOT_H + 20 }}
              >
                {label}
              </span>
            ) : (
              <Skeleton className="absolute h-2.5 w-9 rounded-[3px]" style={{ top: PLOT_H + 20 }} />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function ChartCardsState({
  showRevenue,
  mode,
  ticks,
}: {
  showRevenue: boolean
  mode: 'loading' | 'empty'
  ticks: string[] | null
}) {
  const loading = mode === 'loading'
  return (
    <div className={cn('grid gap-2', showRevenue && 'lg:grid-cols-2')}>
      <ChartCard
        title="Unique visitors"
        headerAction={
          <SegmentedControl
            options={[
              { value: 'visitors', label: 'Visitors' },
              { value: 'pageViews', label: 'Views' },
            ]}
            value="visitors"
            onChange={noop}
          />
        }
        value={loading ? undefined : '0'}
        loading={loading}
      >
        <PlotFrame ticks={ticks} mode={mode} />
      </ChartCard>
      {showRevenue && (
        <ChartCard
          title="Revenue"
          headerAction={<span className="text-xs leading-4 text-muted-foreground">From Stripe</span>}
          value={loading ? undefined : '$0'}
          loading={loading}
        >
          <PlotFrame ticks={ticks} mode={mode} />
        </ChartCard>
      )}
    </div>
  )
}

/* ─── Live activity ───────────────────────────────────────────────────── */

const LIVE_TABS = [
  { value: 'all', label: 'Everything' },
  { value: 'views', label: 'Page views' },
  { value: 'clicks', label: 'Clicks' },
  { value: 'forms', label: 'Forms' },
  { value: 'payments', label: 'Payments' },
]
const LIVE_STATS = ['Page views, 30 min', 'Clicks', 'Form submissions', 'Revenue today', 'Top page now']
const LIVE_STAT_SKELETON = [16, 16, 16, 27, 81]
const LIVE_STAT_EMPTY = ['0', '0', '0', '$0', '–']
/** name, meta, action, detail, pill, time (Paper geometry) */
const LIVE_ROW_SKELETON = [
  [80, 94, 175, 67, 70, 20],
  [80, 54, 189, 27, 90, 20],
  [73, 54, 146, 27, 64, 16],
  [80, 94, 109, 94, 70, 16],
  [73, 54, 182, 74, 80, 16],
  [73, 54, 58, 74, 77, 16],
  [80, 54, 95, 87, 72, 16],
]

function LiveActivityCard({ mode, domain }: { mode: 'loading' | 'empty'; domain: string }) {
  return (
    <Card className="gap-0 py-0">
      <div className="flex flex-wrap items-center gap-3 border-b border-border/60 px-[18px] py-3.5">
        <span
          className={cn(
            'size-2 shrink-0 rounded-full shadow-[0_0_0_4px_var(--success-tint)]',
            mode === 'loading' ? 'bg-live' : 'bg-neutral-300 dark:bg-neutral-600',
          )}
        />
        <OverviewCardTitle>Live activity</OverviewCardTitle>
        <span className="text-xs leading-4 text-muted-foreground">Updates as it happens</span>
        <span className="flex-1" />
        <OverviewTabs ariaLabel="Live activity" options={LIVE_TABS} value="all" onChange={noop} />
      </div>
      <div className="flex flex-col md:flex-row">
        <div className="flex shrink-0 flex-col gap-[18px] border-b border-border/60 bg-muted/60 p-[18px] md:w-[300px] md:border-r md:border-b-0">
          <div className="flex flex-col gap-1">
            <span className="text-xs leading-4 text-muted-foreground">On site now</span>
            {mode === 'loading' ? (
              <span className="flex h-10 items-center">
                <Bar w={36} h={32} />
              </span>
            ) : (
              <span className="text-[32px] font-semibold leading-10 tracking-[-0.03em] text-foreground">0</span>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <div className="flex h-11 items-end gap-[3px]">
              {Array.from({ length: 16 }).map((_, i) =>
                mode === 'loading' ? (
                  <Skeleton key={i} className="h-[18px] flex-1 basis-0 rounded-[2px]" />
                ) : (
                  <span key={i} className="h-0.5 flex-1 basis-0 rounded-[2px] bg-border" />
                ),
              )}
            </div>
            <div className="flex justify-between text-xs leading-4 text-muted-foreground">
              <span>30 min ago</span>
              <span>now</span>
            </div>
          </div>
          <div className="flex flex-col">
            {LIVE_STATS.map((label, i) => (
              <div key={label} className="flex justify-between border-t border-border/60 py-2">
                <span className="text-xs leading-4 text-muted-foreground">{label}</span>
                {mode === 'loading' ? (
                  <span className="flex h-4 items-center">
                    <Bar w={LIVE_STAT_SKELETON[i]} h={12} />
                  </span>
                ) : (
                  <span className="text-xs font-medium leading-4 text-foreground tabular-nums">{LIVE_STAT_EMPTY[i]}</span>
                )}
              </div>
            ))}
          </div>
        </div>
        {mode === 'loading' ? (
          <div className="flex min-w-0 flex-1 flex-col py-1.5">
            {LIVE_ROW_SKELETON.map(([name, meta, action, detail, pill, time], i) => (
              <div key={i} className={cn('flex h-[58px] shrink-0 items-center gap-3 px-[18px]', i > 0 && 'border-t border-border/50')}>
                <Skeleton className="size-[30px] shrink-0 rounded-full" />
                <div className="flex w-[150px] shrink-0 flex-col gap-0.5">
                  <span className="flex h-4 items-center"><Bar w={name} h={13} /></span>
                  <span className="flex h-4 items-center"><Bar w={meta} h={12} /></span>
                </div>
                <div className="hidden min-w-0 flex-1 items-center gap-2 sm:flex">
                  <span className="flex h-4 items-center"><Bar w={action} h={13} /></span>
                  <span className="flex h-4 items-center"><Bar w={detail} h={12} /></span>
                </div>
                <span className="flex-1 sm:hidden" />
                <Skeleton className="h-[22px] shrink-0 rounded-md" style={{ width: pill }} />
                <span className="w-[18px] shrink-0" />
                <span className="flex h-4 w-[34px] shrink-0 items-center justify-end"><Bar w={time} h={12} /></span>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex min-w-0 flex-1 flex-col items-center justify-center gap-1 px-6 py-12 text-center md:py-[120px]">
            <span className="text-[13px] font-medium leading-4 text-ui-ink">Nobody on {domain} yet</span>
            <span className="text-xs leading-4 text-muted-foreground">Each visit, click, form and payment lands here as it happens.</span>
          </div>
        )}
      </div>
      {mode === 'loading' && <OverviewCardFooter label="Open all visitors" />}
    </Card>
  )
}

/* ─── 2×2 breakdown cards ─────────────────────────────────────────────── */

type BreakdownSpec = {
  title: string
  tabs: string[]
  dimension?: string
  footer?: string
  /** label, value, share skeleton widths per row */
  rows: [number, number, number][]
  emptyText: string
}

const BREAKDOWNS: BreakdownSpec[] = [
  {
    title: 'Pages',
    tabs: ['All', 'Landing', 'Exit'],
    dimension: 'Page',
    footer: 'View all pages',
    rows: [[44, 36, 20], [58, 36, 20], [131, 22, 20], [73, 22, 16], [36, 22, 16]],
    emptyText: 'Top pages show here after the first visit.',
  },
  {
    title: 'Acquisition',
    tabs: ['Channels', 'Source / medium', 'Campaigns'],
    dimension: 'Channel',
    footer: 'View all channels',
    rows: [[102, 36, 20], [80, 36, 20], [44, 36, 20], [58, 22, 20], [95, 22, 16]],
    emptyText: 'Channels show here after the first visit.',
  },
  {
    title: 'Technology',
    tabs: ['Browsers', 'OS', 'Devices'],
    rows: [[44, 36, 20], [44, 36, 20], [29, 22, 16], [51, 22, 16]],
    emptyText: 'Browsers show here after the first visit.',
  },
  {
    title: 'Geography',
    tabs: ['Countries', 'Cities', 'Regions'],
    rows: [[95, 36, 20], [44, 36, 20], [102, 22, 20], [51, 22, 16]],
    emptyText: 'Countries show here after the first visit.',
  },
]

function BreakdownStateCard({ spec, mode }: { spec: BreakdownSpec; mode: 'loading' | 'empty' }) {
  return (
    <Card className="gap-0 py-0">
      <div className="flex items-center gap-2.5 px-[18px] py-3.5">
        <OverviewCardTitle className="min-w-0 flex-1 truncate">{spec.title}</OverviewCardTitle>
        <OverviewTabs
          ariaLabel={`${spec.title} breakdown`}
          options={spec.tabs.map((t) => ({ value: t, label: t }))}
          value={spec.tabs[0]}
          onChange={noop}
        />
      </div>
      {spec.dimension && (
        <div className="flex px-[18px] pb-1.5 text-xs leading-4 text-muted-foreground">
          <span className="min-w-0 flex-1">{spec.dimension}</span>
          <span className="w-[70px] shrink-0 text-right">Visitors</span>
          <span className="w-14 shrink-0 text-right">Share</span>
        </div>
      )}
      {mode === 'loading' ? (
        <div className="flex flex-col pb-1.5">
          {spec.rows.map(([label, value, share], i) => (
            <div key={i} className="mx-2.5 flex h-9 shrink-0 items-center px-2">
              <span className="flex h-4 min-w-0 flex-1 items-center"><Bar w={label} h={13} /></span>
              <span className="flex h-4 w-[70px] shrink-0 items-center justify-end"><Bar w={value} h={13} /></span>
              <span className="flex h-4 w-14 shrink-0 items-center justify-end"><Bar w={share} h={12} /></span>
            </div>
          ))}
        </div>
      ) : (
        <div className="mx-2.5 mb-2.5 flex h-9 shrink-0 items-center px-2">
          <span className="min-w-0 flex-1 text-[13px] leading-4 text-ui-meta">{spec.emptyText}</span>
          <span className="w-[70px] shrink-0 text-right text-[13px] font-medium leading-4 text-foreground/25">–</span>
          <span className="w-14 shrink-0 text-right text-xs leading-4 text-foreground/25">–</span>
        </div>
      )}
      {mode === 'loading' && spec.footer && <OverviewCardFooter label={spec.footer} className="mt-auto" />}
    </Card>
  )
}

function OverviewWell({
  mode,
  showRevenue,
  ticks,
  domain,
}: {
  mode: 'loading' | 'empty'
  showRevenue: boolean
  ticks: string[] | null
  domain: string
}) {
  return (
    <div className="well flex flex-col gap-2">
      <OverviewTiles showRevenue={showRevenue} mode={mode} />
      <ChartCardsState showRevenue={showRevenue} mode={mode} ticks={ticks} />
      <LiveActivityCard mode={mode} domain={domain} />
      <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
        {BREAKDOWNS.map((spec) => (
          <BreakdownStateCard key={spec.title} spec={spec} mode={mode} />
        ))}
      </div>
    </div>
  )
}

/** KPI strip + chart cards while the Overview metrics load (no date labels yet). */
export function TrafficChartsLoading({ showRevenue = true }: { showRevenue?: boolean }) {
  return (
    <div className="flex flex-col gap-2" aria-busy="true">
      <OverviewTiles showRevenue={showRevenue} mode="loading" />
      <ChartCardsState showRevenue={showRevenue} mode="loading" ticks={null} />
    </div>
  )
}

/* ─── Public states ───────────────────────────────────────────────────── */

export function OverviewLoading({ showRevenue = true, ticks }: { showRevenue?: boolean; ticks: string[] | null }) {
  return (
    <div className="flex flex-col gap-4" aria-busy="true" aria-label="Loading analytics">
      <div className="flex flex-col gap-1 py-1">
        <div className="flex h-7 items-center">
          <Bar w="60%" h={28} />
        </div>
        <div className="flex h-4 items-center">
          <Bar w="38%" h={13} />
        </div>
      </div>
      <OverviewWell mode="loading" showRevenue={showRevenue} ticks={ticks} domain="" />
    </div>
  )
}

/**
 * The site has never sent a pageview: one sentence says so, the install
 * banner carries the only action, and the report below shows its empty shape.
 */
export function OverviewTrackingNotLive({
  domain,
  rangeLabel,
  banner,
  showRevenue = true,
  ticks,
}: {
  domain: string
  rangeLabel: string
  /** `TrackingNotInstalledBanner` for this site */
  banner: ReactNode
  showRevenue?: boolean
  ticks: string[] | null
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1 py-1">
        <h1 className="text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground">
          The tracker is not live on {domain}, so there is nothing to count yet.
        </h1>
        <p className="text-[13px] leading-4 text-muted-foreground">
          {[domain, rangeLabel].filter(Boolean).join(', ')}. Reports fill in from the first pageview.
        </p>
      </div>
      {banner}
      <OverviewWell mode="empty" showRevenue={showRevenue} ticks={ticks} domain={domain} />
    </div>
  )
}
